import { z } from 'zod';
import {
  YearlyPricesSchema,
  TariffLowVoltageSchema,
  TariffMediumVoltageSchema,
  TaxesSchema,
  PriceListCompositionSchema,
} from './energy-schemas';
import { PresetPayloadSchema } from './preset-schemas';
import { DocumentFormatEnum, LogKindEnum, ErrorLocationEnum } from './enums-definitions';

export const SimulationErrorSchema = z.object({
  type: z.string().nullish(),
  traceback: z.string().nullish(),
  location: ErrorLocationEnum.nullish(),
  message: z.string().nullish(),
  timestamp: z.string().nullish(),
});

export const SimulationLogSchema = z.object({
  timestamp: z.string().nullish(),
  kind: LogKindEnum.nullish(),
  message: z.string().nullish(),
});

export const SimulationMetadataSchema = z.object({
  errors: z.array(SimulationErrorSchema).nullish(),
  logs: z.array(SimulationLogSchema).nullish(),
});

export const SimulationProposalSchema = z
  .object({
    downloadUrl: z.string().url().nullish(),
    documentFormat: DocumentFormatEnum.nullish(),
    createdAt: z.string().nullish(),
  })
  .passthrough();

export const AdditionalCostReportModelSchema = z.object({
  initialExpenses: z.number().nullish(),
  iRecCertificationCostMonthly: z.number().nullish(),
  migrationCost: z.number().nullish(),
});

export const ScenarioYearReportModelSchema = z.object({
  year: z.number().nullish(),
  totalWithoutTaxes: z.number().nullish(),
  totalWithTaxes: z.number().nullish(),
});

export const ScenarioProjectionResultModelSchema = z.object({
  totalCost: z.number().nullish(),
  totalCostMonthAverage: z.number().nullish(),
  totalMigrationCost: z.number().nullish(),
  totalInitialExpenses: z.number().nullish(),
  additionalCost: AdditionalCostReportModelSchema.nullish(),
  totalCostByYear: z.array(ScenarioYearReportModelSchema).nullish(),
  totalCostWithScenarioCostComponent: z.number().nullish(),
  initialInvestment: z.number().nullish(),
});

export const UnitPresetProjectionHighlightSchema = z.object({
  totalConsumationSummationInMwh: z.number().nullish(),
  managementFeeMonthly: z.number().nullish(),
  totalManagementIncome: z.number().nullish(),
  totalEnergyFeeIncome: z.number().nullish(),
  grossMerchandiseValue: z.number().nullish(),
  totalIncome: z.number().nullish(),
});

export const ScenarioProjectionHighlightSchema = z.object({
  totalConsumationSummationInMwh: z.number().nullish(),
  managementFeeMonthly: z.number().nullish(),
  totalManagementIncome: z.number().nullish(),
  totalEnergyFeeIncome: z.number().nullish(),
  grossMerchandiseValue: z.number().nullish(),
  totalIncome: z.number().nullish(),
  initialInvestment: z.number().nullish(),
  totalCost: z.number().nullish(),
});

export const CostCompositionSchema = z.object({
  distributionTariffPeakCost: z.number().nullish(),
  distributionTariffOffPeakCost: z.number().nullish(),
  distributionTariffPeakDiscount: z.number().nullish(),
  distributionTariffOffPeakDiscount: z.number().nullish(),
  electricityTariffCost: z.number().nullish(),
  electricityTariffDiscount: z.number().nullish(),
  demandPeakCost: z.number().nullish(),
  demandOffPeakCost: z.number().nullish(),
  demandPeakDiscount: z.number().nullish(),
  demandOffPeakDiscount: z.number().nullish(),
  securityAndModulationEnergyCharges: z.number().nullish(),
  securityAndModulationEnergyDiscount: z.number().nullish(),
  managementPrice: z.number().nullish(),
  energyPrice: z.number().nullish(),
  energyDiscount: z.number().nullish(),
  taxes: z.number().nullish(),
});

export const ConsumptionDataOfMonthSchema = z.object({
  demandPeak: z.number().nullish(),
  demandOffPeak: z.number().nullish(),
  consumptionPeakOfMonth: z.number().nullish(),
  consumptionOffPeakOfMonth: z.number().nullish(),
});

export const SampleMonthSchema = z.object({
  reference: z.string().nullish(),
  monthYear: z.string().nullish(),
  costComposition: CostCompositionSchema.nullish(),
  consumptionDataOfMonth: ConsumptionDataOfMonthSchema.nullish(),
  totalWithoutTaxes: z.number().nullish(),
  totalWithTaxes: z.number().nullish(),
});

export const UnitYearReportSchema = z.object({
  year: z.number().nullish(),
  totalWithoutTaxes: z.number().nullish(),
  totalWithTaxes: z.number().nullish(),
});

export const ProjectionResultSchema = z.object({
  sampleMonth: SampleMonthSchema.nullish(),
  unitYearReportList: z.array(UnitYearReportSchema).nullish(),
  totalCost: z.number().nullish(),
  totalCostMonthAverage: z.number().nullish(),
  totalMigrationCost: z.number().nullish(),
  totalInitialExpenses: z.number().nullish(),
  additionalCost: AdditionalCostReportModelSchema.nullish(),
  priceListComposition: PriceListCompositionSchema,
});

export const UnitPresetProjectionSchema = z.object({
  Id: z.string().nullish(),
  unitProjectionId: z.string().nullish(),
  unitId: z.string().nullish(),
  scenarioId: z.string().nullish(),
  simulationId: z.string().nullish(),
  calculationMethodStrategy: z.string().nullish(),
  monthReportStrategy: z.string().nullish(),
  unitYearReportStrategy: z.string().nullish(),
  additionalCostReportStrategy: z.string().nullish(),
  unitPresetInput: PresetPayloadSchema.nullish(),
  icms: TaxesSchema.nullish(),
  tariffList: z
    .array(z.union([TariffLowVoltageSchema, TariffMediumVoltageSchema]))
    .optional()
    .nullable(),
  monthToSample: z.string().nullish(),
  projectionResult: ProjectionResultSchema.nullish(),
  unitHighlight: UnitPresetProjectionHighlightSchema.nullish(),
});

export const ScenarioProjectionSchema = z.object({
  Id: z.string().nullish(),
  simulationId: z.string().nullish(),
  scenarioId: z.string().nullish(),
  scenarioIndex: z.number().nullish(),
  scenarioName: z.string().nullish(),
  unitsPresetProjections: z.array(UnitPresetProjectionSchema).nullish(),
  scenarioResult: ScenarioProjectionResultModelSchema.nullish(),
  monthToSample: z.string().nullish(),
  scenarioSummary: ScenarioProjectionHighlightSchema.nullish(),
  scenarioHighlight: ScenarioProjectionHighlightSchema.nullish(),
});

export const SavingInfoSchema = z.object({
  net: z.number().nullish(),
  gross: z.number().nullish(),
});

export const ScenarioEconomyComparisonSchema = z.object({
  savingAbsolute: SavingInfoSchema.nullish(),
  savingPercentage: SavingInfoSchema.nullish(),
  savingAbsoluteByYear: YearlyPricesSchema,
  savingPercentageByYear: YearlyPricesSchema,
  savingByMonth: z.number().nullish(),
  baseScenarioCost: z.number().nullish(),
  baseScenarioCostByYear: z.array(ScenarioYearReportModelSchema).nullish(),
  targetScenarioCost: z.number().nullish(),
  targetScenarioCostByYear: z.array(ScenarioYearReportModelSchema).nullish(),
  scenarioCustom: z.record(z.string(), z.unknown()).nullish(),
  vpl: z.number().nullish(),
});

export const UnitsEconomyComparisonsSchema = z.object({
  unitId: z.string().nullish(),
  savingAbsolute: SavingInfoSchema.nullish(),
  savingPercentage: SavingInfoSchema.nullish(),
  savingAbsoluteByYear: YearlyPricesSchema,
  savingPercentageByYear: YearlyPricesSchema,
  savingByMonth: z.number().nullish(),
  baseScenarioCost: z.number().nullish(),
  baseScenarioCostByYear: YearlyPricesSchema,
  targetScenarioCost: z.number().nullish(),
  targetScenarioCostByYear: YearlyPricesSchema,
});

export const UnitsBenchmarkingSchema = z.object({
  simulationId: z.string().nullish(),
  baseScenarioId: z.string().nullish(),
  targetScenarioId: z.string().nullish(),
  unitsSimulationEconomyComparisonResult: z.array(UnitsEconomyComparisonsSchema).nullish(),
});

export const BenchmarkingHighlightSchema = z.object({
  totalConsumationSummationInMwh: z.number().nullish(),
  managementFeeMonthly: z.number().nullish(),
  totalManagementIncome: z.number().nullish(),
  totalEnergyFeeIncome: z.number().nullish(),
  grossMerchandiseValue: z.number().nullish(),
  totalIncome: z.number().nullish(),
  initialInvestment: z.number().nullish(),
  totalCost: z.number().nullish(),
  savingAbsolute: SavingInfoSchema.nullish(),
  savingPercentage: SavingInfoSchema.nullish(),
});

export const SimulationBenchmarkingCaseSchema = z.object({
  Id: z.string().nullish(),
  simulationId: z.string().nullish(),
  baseScenarioProjectionId: z.string().nullish(),
  targetScenarioProjectionId: z.string().nullish(),
  scenarioEconomyComparison: ScenarioEconomyComparisonSchema.nullish(),
  scenarioBenchmarking: ScenarioEconomyComparisonSchema.nullish(),
  unitsEconomyComparison: UnitsBenchmarkingSchema.nullish(),
  unitsBenchmarking: UnitsBenchmarkingSchema.nullish(),
  comparisonSummary: BenchmarkingHighlightSchema.nullish(),
  benchmarkingHighlight: BenchmarkingHighlightSchema.nullish(),
});

export const ScenarioCostInfoSchema = z.object({
  scenarioProjectionId: z.string().nullish(),
  costValue: z.number().nullish(),
});

export const GeneralHighlightsSchema = z.object({
  mostExpensiveScenario: ScenarioCostInfoSchema.nullish(),
  lessExpensiveScenario: ScenarioCostInfoSchema.nullish(),
  scenariosProjectionsCostRankingAsc: z.array(ScenarioCostInfoSchema).nullish(),
});

export const SimulationHighlightsSchema = z.object({
  id: z.string().nullish(),
  simulationId: z.string().nullish(),
  scenariosCostRanking: GeneralHighlightsSchema.nullish(),
  generalHighlights: GeneralHighlightsSchema.nullish(),
});

export const SimulationOutputsSchema = z.object({
  id: z.string().nullish(),
  simulationId: z.string().nullish(),
  scenariosProjections: z.array(ScenarioProjectionSchema).nullish(),
  benchmarking: z.array(SimulationBenchmarkingCaseSchema).nullish(),
  comparisons: z.array(SimulationBenchmarkingCaseSchema).nullish(),
  highlights: SimulationHighlightsSchema.nullish(),
});
